/**
 * @module Admin
 */

import { Company } from 'rainbow-web-sdk/models/company.model';
import { Service } from 'rainbow-web-sdk/services/service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { Contact } from 'rainbow-web-sdk/models/contact.model';

/** @internal */
export const SDK_ADMIN_SVC = 'SDKAdminService';

export interface SDKAdminService {

    /**
     * Get all companies
     */
    getCompanies(): Promise<Company[]>;

    /**
     * Create a company
     * @param name - Company name
     * @param country - String representing company's country
     * @param state - String representing company's state
     */
    createCompany(name: string, country: string, state?: string): Promise<Company>;

    /**
     * Remove a company by passing its ID
     * @param companyRef - CompanyRef Id or Company Object
     */
    removeCompany(companyRef: string | Company): Promise<void>;

    /**
     * Get users for a given company
     * @param companyRef - ID of Company or the Company instance for which the user is supposed to be created
     */
    getUsersForCompany(companyRef: string | Company): Promise<Contact[]>;

    /**
     * Create a new user for a given company
     * @param login - User login
     * @param firstName - User first name
     * @param lastName - User last name
     * @param password - User password
     * @param companyRef - ID of Company or the Company instance for which the user is supposed to be created
     * @param tags -Array of tags for the user (optional)
     */
    createUserForCompany(login: string, firstName: string, lastName: string, password: string, companyRef: string | Company, tags: string[]): Promise<any>;

    /**
     * Remove a user from its company
     * @param  userId - User identifier
     */
    removeUser(userId: string): Promise<void>;
}

/**
 * @internal
 */
export class SDKAdminServiceRB extends Service implements SDKAdminService {

    private authService: AuthService = AuthService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private loggerService: LoggerService = LoggerService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();

    public static getInstance() { return Service.create(SDK_ADMIN_SVC, SDKAdminServiceRB); }
    public static build() { return new SDKAdminServiceRB(); }
    protected constructor() { super(); }

    public async getCompanies(): Promise<Company[]> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/admin/v1.0/companies`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: "GET", headers });
            if (!response.ok) throw response;
            const data = (await response.json()).data;
            return data.map((companyData: any) => { return Company.createFromData(companyData); });
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "createCompany");
            this.loggerService.error(`[SDKAdminService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async createCompany(name: string, country: string, state?: string): Promise<Company> {
        try {
            const company = Company.create(null, name);
            company.country = country;
            company.economicActivityClassification = "";
            if (state) company.state = state;

            const url = `${config.restServerUrl}/api/rainbow/admin/v1.0/companies?selectedThemeObj=true`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify(Company.prune(company, this.contactService.userContact));
            const response = await fetch(url, { method: "POST", headers, body });
            if (!response.ok) throw response;
            const data = (await response.json()).data;
            return Company.createFromData(data);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "createCompany");
            this.loggerService.error(`[SDKAdminService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async removeCompany(companyRef: string | Company): Promise<void> {
        try {
            const companyId = this.getCompanyId(companyRef);
            const url = `${config.restServerUrl}/api/rainbow/admin/v1.0/companies/${companyId}`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: "DELETE", headers });
            if (!response.ok) throw response;
            return;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "removeCompany");
            this.loggerService.error(`[SDKAdminService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async getUsersForCompany(companyRef: string | Company, detailsLevel: string = null, filters: any[] = null): Promise<any[]> {
        try {
            const headers = this.authService.getRequestHeader();
            const url = new URL(`${config.restServerUrl}/api/rainbow/admin/v1.0/users`);
            url.searchParams.set('companyId', this.getCompanyId(companyRef));
            if (detailsLevel) url.searchParams.set('format', detailsLevel);
            if (filters?.length) {
                filters.forEach(filter => {
                    url.searchParams.set(filter.key, filter.value);
                });
            }
            const response = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) throw response;

            return (await response.json()).data;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getUsersForCompany');
            this.loggerService.error(`[SDKAdminService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async createUserForCompany(login: string, firstName: string, lastName: string, password: string, companyRef: string | Company, tags?: string[]): Promise<any> {
        try {
            const user: any = {};
            user.loginEmail = login;
            user.firstName = firstName;
            user.lastName = lastName;
            user.password = password;
            user.language = "en";
            user.adminType = "undefined";
            user.isActive = true;
            user.isInitialized = true;

            if (typeof companyRef === 'string') user.companyId = (companyRef as string);
            else if (companyRef instanceof Company) user.companyId = (companyRef as Company).id;

            if (tags) user.tags = tags && Array.isArray(tags) ? tags : [tags];

            const url = `${config.restServerUrl}/api/rainbow/admin/v1.0/users`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify(user);
            const response = await fetch(url, { method: "POST", headers, body });
            if (!response.ok) throw response;
            return (await response.json()).data;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "createUserForCompany");
            this.loggerService.error(`[SDKAdminService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async removeUser(userId: string): Promise<void> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/admin/v1.0/users/${userId}`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: "DELETE", headers });
            if (!response.ok) throw response;
            return;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "removeUserFromCompany");
            this.loggerService.error(`[SDKAdminService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async updateUser(user: any): Promise<void> {
        if (!user || !user.id) return;
        try {
            const url = `${config.restServerUrl}/api/rainbow/admin/v1.0/users/${user.id}`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: "PUT",
                headers,
                body: user });
            if (!response.ok) throw response;
            return;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "updateUser");
            this.loggerService.error(`[SDKAdminService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async getServerVersion(): Promise<string> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/admin/v1.0/about`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: "GET", headers });
            if (!response.ok) throw response;
            const data = (await response.json());
            return data.version;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "getServerVersion");
            this.loggerService.error(`[SDKAdminService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    private getCompanyId(companyRef: string | Company): string {
        if (typeof companyRef === 'string') return (companyRef as string);
        else if (companyRef instanceof Company) return (companyRef as Company).id;
        return undefined;
    }
}
